import React, { useState } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import css from "@styled-system/css"
import { motion } from "framer-motion"

import { Box, Flex, Shape, Text, Wrapper } from "components"

export const BANNER_HEIGHT = 32
export const BANNER_HEIGHT_MOBILE = 32

const ARROW_SIZE_MOBILE = 14
const ARROW_SIZE = 16
const ARROW_ICON_HEIGHT_MOBILE = 6
const ARROW_ICON_HEIGHT = 8

function Banner() {
  const { settings } = useStaticQuery(BANNER_QUERY)
  const data = settings && settings.data
  const [isHovering, setIsHovering] = useState(false)

  if (!data || !data.banner_active) return null
  if (!data.banner_text && !(data.banner_link && data.banner_link.url))
    return null

  const url = data.banner_link && data.banner_link.url
  const isExternal = url && /^https?:\/\//i.test(url)
  const target = data.banner_link && data.banner_link.target
  const linkProps = url
    ? isExternal
      ? {
          as: "a",
          href: url,
          target: target || "_blank",
          rel: "noopener noreferrer",
        }
      : { as: Link, to: url }
    : { as: "div" }

  return (
    <Box
      as={motion.div}
      data-component-id="banner"
      width="100%"
      bg="#FF4D01"
      height={[BANNER_HEIGHT_MOBILE, BANNER_HEIGHT]}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.23, 1, 0.32, 1] }}
    >
      <Box
        {...linkProps}
        className={isHovering ? "isHovering" : undefined}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        display="block"
        width="100%"
        height="100%"
        css={css({
          color: "#fff",
          textDecoration: "none",
          "&:focus": { outline: 0 },
          ".arrow--before": {
            transform: "translateX(0)",
            transition: "transform 0ms linear",
          },
          ".arrow--after": {
            transform: [
              `translateX(-${ARROW_SIZE_MOBILE}px)`,
              `translateX(-${ARROW_SIZE}px)`,
            ],
            transition: "transform 0ms linear",
          },
          "&.isHovering": {
            ".arrow--before": {
              transform: [
                `translateX(${ARROW_SIZE_MOBILE}px)`,
                `translateX(${ARROW_SIZE}px)`,
              ],
              transition: "transform 200ms ease-out",
            },
            ".arrow--after": {
              transform: "translateX(0)",
              transition: "transform 200ms ease-out",
            },
          },
        })}
      >
        <Wrapper width="100%" height="100%">
          <Flex
            justifyContent="center"
            alignItems="center"
            height={[BANNER_HEIGHT_MOBILE, BANNER_HEIGHT]}
          >
            <Text
              as="span"
              fontFamily="extended"
              fontSize={[9, 12]}
              fontWeight={700}
              lineHeight={1}
              color="#fff"
              css={css({
                m: 0,
                textTransform: "uppercase",
                letterSpacing: ["0.04em", "0.08em"],
              })}
            >
              {data.banner_text}
            </Text>

            {url && (
              <Box ml="spacing.3" display="inline-flex" alignItems="center">
                <Shape size={[ARROW_SIZE_MOBILE, ARROW_SIZE]} bg="#fff">
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    justifyContent="center"
                    alignItems="center"
                    width={[ARROW_SIZE_MOBILE, ARROW_SIZE]}
                    height={[ARROW_SIZE_MOBILE, ARROW_SIZE]}
                    overflow="hidden"
                    css={{ transform: "translate(-50%, -50%)" }}
                  >
                    <Box
                      className="arrow--before"
                      as="svg"
                      width={[
                        (18 / 14) * ARROW_ICON_HEIGHT_MOBILE,
                        (18 / 14) * ARROW_ICON_HEIGHT,
                      ]}
                      height={[ARROW_ICON_HEIGHT_MOBILE, ARROW_ICON_HEIGHT]}
                      viewBox="0 0 18 14"
                      fill="none"
                      color="#FF4D01"
                    >
                      <title>Arrow Right</title>
                      <path
                        stroke="currentColor"
                        strokeWidth={2}
                        d="M0 7h16M10 13l6-6-6-6"
                      />
                    </Box>
                  </Flex>
                  <Flex
                    position="absolute"
                    top="50%"
                    left="50%"
                    justifyContent="center"
                    alignItems="center"
                    width={[ARROW_SIZE_MOBILE, ARROW_SIZE]}
                    height={[ARROW_SIZE_MOBILE, ARROW_SIZE]}
                    overflow="hidden"
                    css={{ transform: "translate(-50%, -50%)" }}
                  >
                    <Box
                      className="arrow--after"
                      as="svg"
                      width={[
                        (18 / 14) * ARROW_ICON_HEIGHT_MOBILE,
                        (18 / 14) * ARROW_ICON_HEIGHT,
                      ]}
                      height={[ARROW_ICON_HEIGHT_MOBILE, ARROW_ICON_HEIGHT]}
                      viewBox="0 0 18 14"
                      fill="none"
                      color="#FF4D01"
                    >
                      <title>Arrow Right</title>
                      <path
                        stroke="currentColor"
                        strokeWidth={2}
                        d="M0 7h16M10 13l6-6-6-6"
                      />
                    </Box>
                  </Flex>
                </Shape>
              </Box>
            )}
          </Flex>
        </Wrapper>
      </Box>
    </Box>
  )
}

const BANNER_QUERY = graphql`
  {
    settings: prismicSettings {
      data {
        banner_active
        banner_text
        banner_link {
          url
          target
          link_type
        }
      }
    }
  }
`

export default Banner
